import { useState } from 'react';
import sequenceLogo from '../../assets/logos/sequence.svg';
import { send } from '../../api';
import { Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { FactList } from './IntegrationCard';
import { SourceCard, type SourceConfig } from './SourceCard';
import type { IntegrationsState, Webhooks } from './model';

const config: SourceConfig = {
  name: 'sequence', logo: sequenceLogo, role: 'Invoicing · read-only',
  purpose: 'Your billing system: invoices, customers, billing contacts and the customer portal pay link.',
  summary: 'Alder reads invoices, billing contacts and the pay link it puts in reminders from Sequence.',
  reassurance: 'Read-only: Alder never changes invoices in Sequence.',
  fields: [
    { key: 'clientId', label: 'Client id' },
    { key: 'clientSecret', label: 'Client secret', secret: true },
    { key: 'portalBase', label: 'Customer portal URL (optional)', placeholder: 'https://portal.example.com',
      hint: 'The base of the pay link Alder puts in reminders.' },
  ],
};
const message = (e: unknown) => e instanceof Error ? e.message : 'This step could not be completed.';

/**
 * Instant updates are an optional upgrade, never a fault: polling keeps working without them, so the card says how
 * often Alder checks today and what the webhook would add.
 */
function InstantUpdates({ status, intervalMinutes }: { status: Webhooks['sequence']; intervalMinutes: number | null }) {
  const every = intervalMinutes ? `every ${intervalMinutes} minutes` : 'when you choose Sync now';
  return <p className="integration-upgrade"><Zap size={14} aria-hidden/>
    {status.configured
      ? <span><strong>Instant updates: on.</strong> Sequence tells Alder about changes within seconds; the check {every} keeps
        running as a backstop.</span>
      : <span><strong>Instant updates: optional.</strong> Today Alder checks Sequence {every}. Ask your Sequence admin to add
        the webhook in Details for updates within seconds.</span>}
  </p>;
}

/**
 * Optional push from Sequence: the URL and events to register on a webhook in the Sequence dashboard (Settings →
 * Webhooks), and its signing secret ("Show secret key"). Each delivery makes Alder re-read that invoice or customer;
 * amounts in the webhook itself are never used. The interval sync keeps running either way.
 */
function SequenceWebhook({ status, canSave, onChanged }: { status: Webhooks['sequence']; canSave: boolean;
  onChanged: () => Promise<void> }) {
  const [secret, setSecret] = useState(''); const [busy, setBusy] = useState(false);
  const [error, setError] = useState(''); const [done, setDone] = useState('');
  async function run(method: 'PUT' | 'DELETE') {
    const removing = 'Remove the Sequence webhook secret? Deliveries are refused until a new one is saved.';
    if (method === 'DELETE' && !window.confirm(removing)) return;
    setBusy(true); setError(''); setDone('');
    try {
      await send('/app-api/integrations/sequence/webhook', method, method === 'PUT' ? { secret } : undefined);
      setSecret(''); setDone(method === 'PUT' ? 'Signing secret saved.' : 'Signing secret removed.'); await onChanged();
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  }
  const events = status.events.join(', ');
  return <div className="sequence-webhook">
    <p className="details-heading">Webhook for instant updates (optional)</p>
    <p className="details-note">In Sequence, open Settings → Webhooks, add this endpoint with these events, then paste its signing
      secret here.</p>
    <FactList facts={[
      { label: 'Endpoint URL', value: <code>{status.url}</code>, wide: true, copy: status.url },
      { label: 'Events', value: <code>{events}</code>, wide: true, copy: events },
      { label: 'Signing secret', value: status.configured ? <code className="masked">{status.secret ?? 'Saved'}</code> : 'Not added' },
    ]}/>
    <form className="sheet-form" onSubmit={event => { event.preventDefault(); if (secret) void run('PUT'); }}>
      <label><span>Signing secret</span>
        <input type="password" autoComplete="off" spellCheck={false} value={secret} onChange={e => setSecret(e.target.value)}
          placeholder={status.secret ?? 'From "Show secret key" on the Sequence webhook'}/></label>
      <div className="integration-actions">
        <Button size="sm" type="submit" disabled={busy || !canSave || !secret}>Save secret</Button>
        {status.configured && <Button size="sm" variant="danger" type="button" disabled={busy || !canSave}
          onClick={() => void run('DELETE')}>Remove</Button>}
      </div>
    </form>
    {error && <p className="integration-error" role="alert">{error}</p>}
    {done && <p className="ok" role="status">{done}</p>}
  </div>;
}

export function SequenceCard(props: { state: IntegrationsState; onChanged: () => Promise<void>; onSyncNow: () => void;
  syncing: boolean }) {
  const webhook = props.state.live && props.state.connectors.sequence.source !== 'none' ? props.state.webhooks?.sequence : undefined;
  const interval = props.state.schedule?.scheduled ? props.state.schedule.intervalMinutes : null;
  return <SourceCard config={config} {...props}
    details={webhook && <SequenceWebhook status={webhook} canSave={props.state.canSave} onChanged={props.onChanged}/>}>
    {webhook && <InstantUpdates status={webhook} intervalMinutes={interval}/>}
  </SourceCard>;
}
